import { useMemo } from 'react';
import Fuse, { IFuseOptions } from 'fuse.js';
import { isDefined } from '@shared/helpers/array.helpers';
import { useObjectMemoDeep } from './use-object.hooks';
import { omit } from '@shared/helpers/object.helpers';
import { useDebounce } from './use-utils.hooks';

interface IUseSearchProps<T> extends IFuseOptions<T> {
  /**
   * Pass number to debounce the string search.
   */
  debounce?: number;
}

export const useSearch = <T>(
  items: T[] = [],
  searchString: string = '',
  options: IUseSearchProps<T> = {},
) => {
  const fuseOptions = useObjectMemoDeep(omit(options, 'debounce'));
  const debounceMs = options.debounce ?? 0;

  const debouncedString = useDebounce(searchString, debounceMs);

  // When debounce is 0, use searchString directly to avoid one-render lag from setTimeout(0)
  const effectiveSearchString =
    debounceMs === 0 ? searchString : debouncedString;

  // Only create a new fuse instance if the options have changed
  const fuse = useMemo(
    () =>
      new Fuse(items, {
        includeScore: true,
        shouldSort: false,
        minMatchCharLength: 1,
        threshold: 0.2,
        distance: 1000, // Distance * threshold is the amount of characters searched
        keys: ['id', 'name', 'value', 'label'],
        ...fuseOptions,
      }),
    [fuseOptions, items],
  );

  const filteredItems = useMemo(
    () =>
      effectiveSearchString
        ? fuse
            .search(effectiveSearchString)
            .map((foundItem) => items[foundItem.refIndex])
            .filter(isDefined)
        : items,
    [fuse, items, effectiveSearchString],
  );

  // Only return new array if the filtered items have changed
  return useObjectMemoDeep(filteredItems);
};
